import type { DeliveryOptions as DeliveryOptionsSchema } from '../../../../amplienceTypes/schemas/imported/product-delivery-options-schema';
import type { DeliveryOption } from '../../../../utilities/graphql/codegen';
import type { ReactElement } from 'react';

import styled, { css } from 'styled-components';

import { ContentUspDeliveryConfig, ContentUspLocationConfig } from './ProductUSPs';
import { useStaticContext } from '../../../../utilities/context/static/StaticContext';
import { SiteType } from '../../../../utilities/graphql/codegen';
import { media } from '../../../shared/core/styles';
import { useAppContext } from '~/utilities/context/static/AppContext';
import { getSiteStyles } from '~/utilities/helpers';

interface ProductDeliveryOptionsI {
  deliveryOptions: (DeliveryOption | null)[];
  content: DeliveryOptionsSchema | undefined;
}

const costStyle = css`
  flex-grow: 1;
  text-align: right;
  font-weight: 700;
`;

const S = {
  Wrapper: styled.div`
    display: flex;
    flex-direction: column;
  `,

  Item: styled.div`
    display: flex;
    flex-direction: row;
    line-height: 16px;
    color: ${({ theme }) => theme.colors.content.primary};
    font-size: ${({ theme }) => theme.font.size[2]};

    :not(:last-child) {
      margin-bottom: ${({ theme }) => theme.spacing.y.xs};
    }
  `,

  IconWrapper: styled.span``,

  ItemIcon: styled.span<{ $iconName: string }>`
    &::before {
      font-family: gstar-icons;
      content: '${'\\F1BD'}';
      text-align: center;
      vertical-align: middle;

      ${({ $iconName }) =>
        ($iconName === 'icon-usp-delivery' &&
          css`
            ${({ theme }) => ContentUspDeliveryConfig[theme.siteType]}
            margin-right: ${({ theme }) => theme.spacing.x.xs};
          `) ||
        ($iconName === 'icon-bike' &&
          css`
            content: '${'\\F246'}';
            margin-right: ${({ theme }) => theme.spacing.x.xs};
          `) ||
        ($iconName === 'icon-usp-location' &&
          css`
            ${({ theme }) => ContentUspLocationConfig[theme.siteType]}
            padding-left: 3px;

            @media ${({ theme }) => media(theme).lessThan('sm')} {
              margin-right: 5px;
            }
          `)}
    }
  `,

  ItemName: styled.div`
    font-weight: 300;
  `,

  HasCost: styled.span`
    ${costStyle}
  `,

  FreeCost: styled.span`
    color: ${({ theme }) => theme.colors.content.sale};
    ${({ theme }) =>
      getSiteStyles(theme.siteType, {
        [SiteType.Aaf]: css`
          color: ${({ theme }) => theme.colors.content.primary};
        `,
      })}

    ${costStyle};
  `,
};

export const ProductDeliveryOptions = ({
  deliveryOptions,
  content,
}: ProductDeliveryOptionsI): ReactElement => {
  const {
    configuration: { enablePdpDeliveryOptions },
  } = useStaticContext();
  const { isGStar } = useAppContext();
  const {
    configuration: { currentCurrencySymbol },
  } = useStaticContext();

  return enablePdpDeliveryOptions ? (
    <S.Wrapper data-testid="product-delivery-options">
      {deliveryOptions.map((option, index) => (
        <S.Item key={index}>
          {isGStar && (
            <S.IconWrapper>
              <S.ItemIcon $iconName={option?.icon || 'icon-usp-delivery'} />
            </S.IconWrapper>
          )}
          <S.ItemName>{option?.name}</S.ItemName>
          {option?.deliveryCost && option.deliveryCost > 0 ? (
            <S.HasCost>
              {currentCurrencySymbol ?? '$'} {option.deliveryCost}
            </S.HasCost>
          ) : (
            <S.FreeCost>{content?.freeLabel || ''}</S.FreeCost>
          )}
        </S.Item>
      ))}
    </S.Wrapper>
  ) : (
    <></>
  );
};
